import * as React from 'react';
import type { GetStaticProps } from 'next';
import { contentfulAPIURL } from '@/clients/contentful';
import { createApolloClient } from '@/clients/apollo';
import { queryTwoForOnePageBySlug, queryFooter, queryNavbar } from '@/clients/queries';
import { FooterQuery, NavbarQuery, PageBySlugQuery } from 'types/generated';
import { getOr } from '@/utils/options';
import TveirFyrirEinnPage from '@/contentful/TveirFyrirEinnPage';
import { TwoForOneProvider } from '@/components/2fyrir1/TwoForOneContext';
import PageContainer from '@/components/PageContainer';
import { centeredMainGridClasses } from '@/styles/grid';
import { Label } from 'components/src/typography';
import { getRadioSchedules } from '@/clients/RadioUtils';
import { RadioSchedule } from '@/models/RadioTypes';
import { Log } from '@/logging/datadog';

type Props = {
  query: PageBySlugQuery;
  navbar: NavbarQuery;
  footer: FooterQuery;
  radioSchedules: Record<string, RadioSchedule[]>;
};

const RouteComponent = ({ query, navbar, footer, radioSchedules }: Props) => {
  const pageCollection = query.pageCollection?.items[0];

  if (!pageCollection) {
    return (
      <div className={`${centeredMainGridClasses} mt-16 text-center`}>
        <Label>{'404 /2fyrir1 page not found'}</Label>
      </div>
    );
  }

  return (
    <PageContainer
      title={getOr(pageCollection.title, 'Sýn')}
      metaTags={pageCollection.metaTags}
      contactFormSetting={pageCollection.contactFormSetting}
      navbar={navbar}
      footer={footer}
      radioSchedules={radioSchedules}
    >
      <TwoForOneProvider>
        <TveirFyrirEinnPage query={query} />
      </TwoForOneProvider>
    </PageContainer>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const slug = '2fyrir1';
  try {
    const apiUrl = contentfulAPIURL(
      process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID || '',
      process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT || ''
    );
    const apolloClient = createApolloClient(apiUrl, process.env.NEXT_PUBLIC_CONTENTFUL_CD_TOKEN || '');

    const contentfulPage = await queryTwoForOnePageBySlug(apolloClient, slug);
    const footer = await queryFooter(apolloClient);
    const navbar = await queryNavbar(apolloClient);

    return {
      props: {
        navbar: navbar.data,
        query: contentfulPage.data,
        footer: footer.data,
        radioSchedules: await getRadioSchedules(),
      },
    };
  } catch (err) {
    Log.error((err as Error).message || '', err);
    throw err;
  }
};

export default RouteComponent;
